import { gsap } from 'gsap';

const specialtyCards = Array.from(document.querySelectorAll('.specialty-card'));
const valueCards = Array.from(document.querySelectorAll('.value-card'));
const glowCards = [...specialtyCards, ...valueCards];

if (glowCards.length) {
  const hoverMedia = window.matchMedia('(hover: hover) and (pointer: fine)');
  const desktopMedia = window.matchMedia('(min-width: 1034px)');
  const tabletMedia = window.matchMedia('(min-width: 641px) and (max-width: 1033px)');
  const mobileMedia = window.matchMedia('(max-width: 640px)');
  const reducedMotionMedia = window.matchMedia('(prefers-reduced-motion: reduce)');
  let cleanupHandlers = [];

  const getCardName = (card) => (card.classList.contains('value-card') ? 'value-card' : 'specialty-card');

  const ensureGlow = (card) => {
    const cardName = getCardName(card);
    let glow = card.querySelector(`.${cardName}__glow`);

    if (!glow) {
      glow = document.createElement('span');
      glow.className = `${cardName}__glow`;
      glow.setAttribute('aria-hidden', 'true');
      card.prepend(glow);
    }

    return glow;
  };

  const resetCard = (card) => {
    card.classList.remove('is-glow-active');

    if (card.classList.contains('value-card')) {
      card.classList.remove('is-interactive-active');
    }

    const cardName = getCardName(card);
    const glow = card.querySelector(`.${cardName}__glow`);

    if (glow) {
      gsap.set(glow, { x: 0, y: 0 });
    }
  };

  const destroy = () => {
    cleanupHandlers.forEach((cleanup) => cleanup());
    cleanupHandlers = [];
    glowCards.forEach(resetCard);
  };

  const bindTabletInteractiveState = (cards, { preventNavigation = false } = {}) => {
    let activeCard = cards[0] ?? null;

    if (activeCard) {
      activeCard.classList.add('is-interactive-active');
    }

    return cards.map((card) => {
      const handleClick = (event) => {
        if (!tabletMedia.matches) {
          return;
        }

        const isAlreadyActive = activeCard === card;

        if (preventNavigation && !isAlreadyActive) {
          event.preventDefault();
        }

        if (activeCard && !isAlreadyActive) {
          activeCard.classList.remove('is-interactive-active');
        }

        card.classList.add('is-interactive-active');
        activeCard = card;
      };

      card.addEventListener('click', handleClick);

      return () => {
        card.removeEventListener('click', handleClick);
      };
    });
  };

  const init = () => {
    destroy();

    if (tabletMedia.matches) {
      cleanupHandlers = [
        ...bindTabletInteractiveState(valueCards),
      ];

      return;
    }

    if (!desktopMedia.matches || !hoverMedia.matches || reducedMotionMedia.matches) {
      return;
    }

    glowCards.forEach(ensureGlow);

    cleanupHandlers = glowCards.map((card) => {
      const glow = ensureGlow(card);

      const glowXTo = gsap.quickTo(glow, 'x', {
        duration: 0.72,
        ease: 'power3.out',
        overwrite: 'auto',
      });
      const glowYTo = gsap.quickTo(glow, 'y', {
        duration: 0.86,
        ease: 'power3.out',
        overwrite: 'auto',
      });
      let bounds = null;

      const updateBounds = () => {
        bounds = card.getBoundingClientRect();
      };

      const moveGlow = (event) => {
        if (!bounds) {
          updateBounds();
        }

        const nextX = event.clientX - bounds.left - bounds.width / 2;
        const nextY = event.clientY - bounds.top - bounds.height / 2;

        glowXTo(nextX);
        glowYTo(nextY);
      };

      const handlePointerEnter = (event) => {
        updateBounds();
        card.classList.add('is-glow-active');
        moveGlow(event);
      };

      const handlePointerLeave = () => {
        card.classList.remove('is-glow-active');
        glowXTo(0);
        glowYTo(0);
      };

      const handleResize = () => {
        bounds = null;
      };

      card.addEventListener('pointerenter', handlePointerEnter);
      card.addEventListener('pointermove', moveGlow, { passive: true });
      card.addEventListener('pointerleave', handlePointerLeave);
      window.addEventListener('resize', handleResize, { passive: true });

      return () => {
        card.removeEventListener('pointerenter', handlePointerEnter);
        card.removeEventListener('pointermove', moveGlow);
        card.removeEventListener('pointerleave', handlePointerLeave);
        window.removeEventListener('resize', handleResize);
        handlePointerLeave();
      };
    });
  };

  init();

  const handleMediaChange = () => {
    init();
  };

  hoverMedia.addEventListener('change', handleMediaChange);
  desktopMedia.addEventListener('change', handleMediaChange);
  tabletMedia.addEventListener('change', handleMediaChange);
  mobileMedia.addEventListener('change', handleMediaChange);
  reducedMotionMedia.addEventListener('change', handleMediaChange);

  window.addEventListener('pagehide', () => {
    hoverMedia.removeEventListener('change', handleMediaChange);
    desktopMedia.removeEventListener('change', handleMediaChange);
    tabletMedia.removeEventListener('change', handleMediaChange);
    mobileMedia.removeEventListener('change', handleMediaChange);
    reducedMotionMedia.removeEventListener('change', handleMediaChange);
    destroy();
  }, { once: true });
}
